import { useEffect, useState } from 'react';

const DISMISS_KEY = 'smda-install-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari's legacy standalone flag.
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Captures the browser's `beforeinstallprompt` event so the app can show its
 * own tasteful, Persian-copy install call-to-action instead of relying on
 * the browser's default mini-infobar. Dismissal is persisted (localStorage)
 * so the prompt never nags on every visit — only Chromium-based browsers
 * fire this event; iOS Safari and already-installed contexts simply never
 * become "installable".
 */
export function useInstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isDismissed, setIsDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === 'true',
  );

  useEffect(() => {
    if (isStandalone()) return;

    function handleBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
    }
    function handleAppInstalled() {
      setDeferredEvent(null);
      localStorage.setItem(DISMISS_KEY, 'true');
      setIsDismissed(true);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  async function promptInstall() {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    setDeferredEvent(null);
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, 'true');
    setIsDismissed(true);
  }

  return {
    isInstallable: Boolean(deferredEvent) && !isDismissed,
    promptInstall,
    dismiss,
  };
}
